<script setup lang="ts">
import { ref, computed } from "vue";
import config from "~/config.json";
import Footer from "~/components/layout/Footer.vue";
import Navbar from "~/components/layout/Navbar.vue";
import LayoutSection from "~/components/layout/Section.vue";
import UiButton from "~/components/ui/Button.vue";
import { type DropdownItem } from "~/components/ui/Dropdown.vue";
import ProjectsGalleryGrid from "~/components/layout/ProjectsGalleryGrid.vue";
import ReviewsGrid from "~/components/layout/ReviewsGrid.vue";
import Contact from "~/components/layout/Contact.vue";

useHead({
  title: "Domů"
});

const serviceTypes: string[] = config["services"].map((service) => service.id);
const servicesDropdownItems: DropdownItem[] = config["services"].flatMap((service) => {
  return service["services-list"].map((item) => ({
    label: item.title,
    value: item.title
  }));
});
const selectedServiceType = ref<string>(serviceTypes[0] || "");
const selectedServices = computed(() => {
  const serviceSection = config["services"].find((section: any) => section.id === selectedServiceType.value);

  return serviceSection ? serviceSection["services-list"] : [];
});

const setServiceType = (serviceType: string): void => {
  if (!serviceTypes.includes(serviceType)) return;

  selectedServiceType.value = serviceType;
};
</script>

<template>
  <LayoutSection id="hero" background="/images/hero-background.jpg" parallax>
    <Navbar />
    <div class="group">
      <div class="content">
        <h1>Zemní a výkopové práce, úprava terénu, odvoz dovoz zeminy</h1>
        <p>Realizujeme zemní a výkopové práce a úpravy terénu ve Velkých Bílovicích a okolí Břeclavi, Mikulova a
          Hustopečí. Zajistíme také spolehlivý odvoz i dovoz zeminy přesně podle vašich potřeb.</p>

        <div class="buttons">
          <UiButton type="primary" variant="solid" href="#contact" trailing-icon="material-symbols:arrow-right-rounded">
            Kontaktujte nás</UiButton>
          <UiButton type="secondary" variant="solid" href="/projects">Projekty</UiButton>
        </div>
      </div>

      <div class="reviews">
        <div class="source">
          <Icon class="google" name="simple-icons:google" />
        </div>
        <span>Celkové hodnocení: 5.0
          <Icon name="material-symbols:star-rounded" class="star" />
        </span>
      </div>
    </div>

    <img src="/images/hero-illustration.svg" title="Ilustrace" class="illustration" aria-label="Ilustrace"
      fetchpriority="high" loading="eager" provider="static" />
  </LayoutSection>

  <LayoutSection id="services">
    <div class="group">
      <div class="section-head">
        <span>Služby + ceník</span>
        <h2>Přehled našich služeb a ceníku</h2>
      </div>

      <div class="buttons">
        <UiButton type="primary" variant="solid" href="#contact" trailing-icon="material-symbols:arrow-right-rounded">
          Napište nám</UiButton>
      </div>
    </div>

    <div class="info">
      <div class="services-list">
        <ul class="nav">
          <li v-for="serviceSection in config['services']" :key="serviceSection.id"
            @click="() => setServiceType(serviceSection.id)"
            :class="{ active: selectedServiceType === serviceSection.id }">
            <span class="line" />
            {{ serviceSection.title }}
          </li>
        </ul>

        <ul class="items">
          <li v-for="(service, serviceIndex) in selectedServices" :key="serviceIndex">
            <div class="content">
              <h3>{{ service.title }}</h3>
              <p>{{ service.description }}</p>
            </div>

            <div class="img-box">
              <img :src="service.image" :alt="service['image-alt']" loading="lazy" />
            </div>

            <p class="price"><span>Cena:</span><span class="number">{{ service.price.amount }}</span>{{
              service.price.currency }} / {{ service.price.unit }}</p>
          </li>
        </ul>
      </div>

      <div class="other">
        <div class="fees">
          <h4>Poplatky:</h4>
          <p>
            Odvoz mimo Velké Bílovice - <span class="highlight">30 Kč</span> / km
          </p>
        </div>
      </div>
    </div>
  </LayoutSection>

  <LayoutSection id="why-us" background="/images/section-background.jpg">
    <div class="section-head">
      <span>Proč si vybrat nás</span>
      <h2>Proč se spolehnout právě na naše služby</h2>
    </div>

    <div class="content">
      <div class="column">
        <ul>
          <li>
            <Icon class="icon" name="material-symbols:engineering-rounded" />
            <h3>Zkušenosti a odbornost</h3>
            <p>Více než 10 let zkušeností se zemními a výkopovými pracemi pro soukromé i firemní projekty.</p>
          </li>

          <li>
            <Icon class="icon" name="material-symbols:construction-rounded" />
            <h3>Moderní technika</h3>
            <p>Používáme spolehlivé stroje a vybavení pro efektivní a přesné provedení každé zakázky.</p>
          </li>

          <li>
            <Icon class="icon" name="material-symbols:location-on-rounded" />
            <h3>Znalost regionu</h3>
            <p>Působíme ve Velkých Bílovicích a okolí Břeclavi, Mikulova a Hustopečí.</p>
          </li>
        </ul>
      </div>

      <div class="column images">
        <div class="images">
          <div class="img-box">
            <img src="/images/why-us-1.jpg" alt="Proč my 1" loading="lazy" />
          </div>

          <div class="experience">
            <span>20+</span>
            let zkušeností
          </div>

          <div class="img-box">
            <img src="/images/why-us-1.jpg" alt="Proč my 2" loading="lazy" />
          </div>
        </div>
      </div>

      <div class="column">
        <ul>
          <li>
            <Icon class="icon" name="material-symbols:handshake-rounded" />
            <h3>Individuální přístup</h3>
            <p>Ke každé zakázce přistupujeme individuálně a hledáme nejvhodnější řešení.</p>
          </li>

          <li>
            <Icon class="icon" name="material-symbols:schedule-rounded" />
            <h3>Rychlá realizace</h3>
            <p>Dodržujeme domluvené termíny a pracujeme efektivně bez zbytečných prodlev.</p>
          </li>

          <li>
            <Icon class="icon" name="material-symbols:verified-rounded" />
            <h3>Kvalita a spolehlivost</h3>
            <p>Důraz na kvalitně odvedenou práci, bezpečnost a dlouhodobou spokojenost zákazníků.</p>
          </li>
        </ul>
      </div>
    </div>
  </LayoutSection>

  <LayoutSection id="projects">
    <div class="group">
      <div class="section-head">
        <span>Galerie projektů</span>
        <h2>Fotografie našich realizovaných projektů</h2>
      </div>

      <div class="right">
        <p>Fotografie našich realizovaných zemních a výkopových prací, úprav terénu a odvozu či dovozu zeminy.</p>

        <div class="buttons">
          <UiButton type="secondary" variant="solid" href="/projects">Všechny projekty</UiButton>
          <UiButton type="primary" variant="solid" href="#contact" trailing-icon="material-symbols:arrow-right-rounded">
            Napište nám</UiButton>
        </div>
      </div>
    </div>

    <ProjectsGalleryGrid :max-rows-per-page="2" :pagination="false" />
  </LayoutSection>

  <LayoutSection id="statistics" background="/images/statistics-background.jpg" parallax>
    <ul>
      <li>
        <span>83</span>
        Spokojených zákazníků
      </li>
      <li>
        <span>21</span>
        Let zkušeností
      </li>
      <li>
        <span>120+</span>
        Realizovaných projektů
      </li>
      <li>
        <span>50 000+</span>
        Hodin strávených v terénu
      </li>
    </ul>
  </LayoutSection>

  <LayoutSection id="about-us">
    <div class="images">
      <div class="img-box">
        <img src="/images/why-us-1.jpg" alt="O nás 1" loading="lazy" />
      </div>

      <div class="column">
        <div class="img-box">
          <img src="/images/why-us-3.jpeg" alt="O nás 2" loading="lazy" />
        </div>
        <div class="social-links">
          <UiButton type="secondary" variant="ghost" name="Zavolat" icon="mdi:phone" href="tel:+420605583489" target="_blank" />
          <UiButton type="secondary" variant="ghost" name="Napsat e-mail" icon="mdi:email" href="mailto:Palkajaro@seznam.cz" target="_blank" />
        </div>
      </div>
    </div>

    <div class="content">
      <div class="section-head">
        <span>O nás</span>
        <h2>Naše cesta a hodnoty</h2>
      </div>

      <p>
        Jsme poctivá česká firma, která se více než 20 let věnuje zemním a výkopovým pracím, úpravám terénu a dopravě
        zeminy. Naším cílem je poskytovat spolehlivé služby s důrazem na kvalitu a efektivitu.
        <br><br>
        Věříme v lidský přístup ke každému zákazníkovi. Každý projekt řešíme individuálně, s ohledem na potřeby klienta
        i specifika pozemku. Moderní technika a zkušený tým zajišťují, že práce probíhá bezpečně, rychle a přesně.
        <br><br>
        Naše firma staví na důvěře, odbornosti a dlouhodobých vztazích. Spokojení zákazníci a kvalitně provedené
        projekty jsou pro nás nejlepší vizitkou.
      </p>

      <ul class="values">
        <li>
          <div class="icon">
            <Icon name="material-symbols:pin-drop-rounded" />
          </div>
          Poctivá česká firma
        </li>
        <li>
          <div class="icon">
            <Icon name="material-symbols:group-rounded" />
          </div>
          Lidský přístup
        </li>
      </ul>
    </div>
  </LayoutSection>

  <LayoutSection id="reviews">
    <div class="group">
      <div class="section-head">
        <span>Recenze</span>
        <h2>Recenze a zkušenosti našich zákazníků</h2>
      </div>

      <div class="buttons">
        <UiButton type="secondary" variant="solid" href="/reviews">Všechny recenze</UiButton>
        <UiButton type="primary" variant="solid" href="#contact" trailing-icon="material-symbols:arrow-right-rounded">
          Napište nám</UiButton>
      </div>
    </div>

    <ReviewsGrid :max-rows-per-page="1" :pagination="false" />
  </LayoutSection>

  <Contact :services-dropdown-items="servicesDropdownItems" />
  <Footer />
</template>

<style scoped lang="scss">
#hero {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 130px;
  height: 100svh;
  min-height: 1030px;
  position: relative;
  z-index: 1;

  .illustration {
    position: absolute;
    width: 45%;
    bottom: 0;
    right: 3%;
    transform: translateY(11%);
    pointer-events: none;
    user-select: none;
  }

  .group {
    display: flex;
    flex-direction: column;
    justify-content: center;
    flex-grow: 1;
    gap: 100px;

    .content {
      display: flex;
      flex-direction: column;
      gap: 30px;
      max-width: 50%;
      z-index: 1;

      h1 {
        font-size: 51px;
        font-weight: 700;
        color: rgb(var(--title-dark-color));
      }

      p {
        font-size: 18px;
        color: rgb(var(--text-dark-color));
      }

      .buttons {
        display: flex;
        gap: 20px;
        flex-wrap: wrap;
      }
    }

    .reviews {
      display: flex;
      align-items: center;
      gap: 10px;
      background: rgba(var(--main-background), 0.2);
      padding: 20px;
      border-radius: var(--small-border-radius);
      font-size: 15px;
      font-weight: 500;
      color: rgb(var(--text-dark-color));
      width: fit-content;

      .source {
        display: flex;
        align-items: center;
        justify-content: center;
        min-width: 40px;
        min-height: 40px;
        border-radius: 50%;
        background: rgba(var(--main-background), 0.2);

        .google {
          font-size: 24px;
          line-height: 0;
        }
      }

      span {
        display: flex;
        align-items: center;
        gap: 5px;

        .star {
          font-size: 21px;
          color: rgb(var(--star-color));
        }
      }
    }
  }
}

#services {
  display: flex;
  flex-direction: column;
  gap: 60px;

  .group {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 30px;

    .section-head {
      flex: 1;
    }

    .buttons {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
    }
  }

  .info {
    display: flex;
    flex-direction: column;
    gap: 30px;
    width: 100%;

    .other {
      display: flex;
      flex-direction: column;
      gap: 20px;
      flex: 1;

      .fees {
        display: flex;
        flex-direction: column;
        gap: 10px;

        h4 {
          font-size: 18px;
          font-weight: 700;
          color: rgb(var(--text-light-color));
        }

        p {
          font-size: 18px;
          color: rgb(var(--text-light-color));

          .highlight {
            font-weight: 700;
            color: rgb(var(--main-color));
          }
        }
      }

      .note {
        display: flex;
        flex-direction: column;
        gap: 10px;

        h4 {
          font-size: 18px;
          font-weight: 700;
          color: rgb(var(--text-light-color));
        }

        p {
          font-size: 18px;
          color: rgb(var(--text-light-color));

          .highlight {
            font-weight: 700;
            color: rgb(var(--main-color));
          }
        }
      }
    }

    .services-list {
      display: flex;
      flex-direction: row;
      gap: 60px;
      flex-wrap: wrap;

      .nav {
        display: flex;
        flex-direction: column;
        gap: 20px;
        list-style: none;
        width: fit-content;

        li {
          display: flex;
          align-items: center;
          gap: 20px;
          font-weight: 700;
          font-size: 18px;
          color: rgb(var(--text-light-color));
          transition: 0.2s;
          cursor: pointer;

          .line {
            width: 30px;
            height: 2px;
            background: rgb(var(--text-light-color));
            border-radius: var(--small-border-radius);
            transition: 0.2s;
          }

          &:hover,
          &.active {
            color: rgb(var(--main-color));

            .line {
              background: rgb(var(--main-color));
            }
          }
        }
      }

      .items {
        display: flex;
        flex-direction: row;
        gap: 40px;
        list-style: none;
        flex-wrap: wrap;
        flex: 1;

        li {
          display: flex;
          flex-direction: column;
          gap: 20px;
          flex: 1;
          min-width: 250px;

          .content {
            display: flex;
            flex-direction: column;
            gap: 10px;
            padding: 0 10px;
          }

          .img-box {
            width: 100%;
            border-radius: var(--large-border-radius);
            overflow: hidden;
            position: relative;
            height: 250px;
            min-height: 250px;
            flex: 1;

            img {
              width: 100%;
              height: 100%;
              object-fit: cover;
              position: absolute;
              top: 0;
              left: 0;
            }
          }

          h3 {
            font-size: 21px;
            font-weight: 700;
            color: rgb(var(--mini-title-light-color));
          }

          p {
            font-size: 18px;
            color: rgb(var(--text-light-color));
          }

          .price {
            display: flex;
            align-items: center;
            gap: 10px;
            font-size: 18px;
            color: rgb(var(--text-light-color));
            padding: 0 10px;

            span {
              font-weight: 700;
            }

            .number {
              font-weight: 700;
              font-size: 21px;
              color: rgb(var(--main-color));
            }
          }
        }
      }
    }
  }
}

#why-us {
  display: flex;
  flex-direction: column;
  gap: 60px;

  .section-head {
    align-items: center;
    text-align: center;

    h2 {
      max-width: 500px;
    }
  }

  .content {
    display: flex;
    flex-direction: row;
    gap: 60px;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;

    .column {
      flex: 1;
      min-width: 200px;

      ul {
        display: flex;
        flex-direction: column;
        gap: 30px;
        list-style: none;

        li {
          display: flex;
          flex-direction: column;
          gap: 15px;

          .icon {
            font-size: 36px;
            color: rgb(var(--main-color));
          }

          h3 {
            font-size: 18px;
            font-weight: 700;
            color: rgb(var(--title-light-color));
          }

          p {
            font-size: 18px;
            color: rgb(var(--text-light-color));
          }
        }
      }
    }

    .images {
      position: relative;
      display: flex;
      flex-direction: row;
      gap: 20px;
      justify-content: center;
      align-items: center;
      max-width: 100%;

      .img-box {
        width: 100%;
        max-width: 300px;
        aspect-ratio: 3 / 5;
        border-radius: var(--large-border-radius);
        overflow: hidden;
        position: relative;

        &:first-child {
          margin-bottom: 50px;
        }

        &:last-child {
          margin-top: 50px;
        }

        img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
      }

      .experience {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 5px;
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        background: rgb(var(--main-color));
        padding: 20px;
        border-radius: var(--normal-border-radius);
        text-align: center;
        font-size: 18px;
        color: rgb(var(--title-light-color));
        z-index: 2;

        span {
          font-size: 24px;
          font-weight: 700;
        }
      }
    }
  }
}

#reviews {
  display: flex;
  flex-direction: column;
  gap: 60px;

  .group {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 30px;

    .section-head h2 {
      max-width: 600px;
    }

    .buttons {
      display: flex;
      gap: 20px;
      flex-wrap: wrap;
    }
  }
}

#statistics {
  ul {
    display: flex;
    flex-direction: row;
    gap: 60px;
    justify-content: space-between;
    flex-wrap: wrap;
    list-style: none;
    width: 100%;

    li {
      min-width: fit-content;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 10px;
      font-size: 18px;
      flex: 1;
      color: rgb(var(--mini-title-dark-color));

      span {
        font-size: 48px;
        font-weight: 700;
        color: rgb(var(--title-dark-color));
      }
    }
  }
}

#about-us {
  display: flex;
  flex-direction: row;
  gap: 60px;

  .images {
    display: flex;
    flex-direction: row;
    gap: 20px;
    flex: 1;

    .img-box {
      width: 100%;
      border-radius: var(--large-border-radius);
      overflow: hidden;
      position: relative;
      min-height: 200px;
      flex: 1;

      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        position: absolute;
        top: 0;
        left: 0;
      }
    }

    .column {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 20px;

      .social-links {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-wrap: wrap;
        gap: 10px;
        background: rgb(var(--card-background));
        padding: 10px;
        width: 100%;
        border-radius: var(--large-border-radius);
      }
    }
  }

  .content {
    display: flex;
    flex-direction: column;
    gap: 30px;
    flex: 1;

    p {
      font-size: 18px;
      color: rgb(var(--text-light-color));
    }

    .values {
      display: flex;
      flex-direction: row;
      gap: 30px;
      list-style: none;
      flex-wrap: wrap;

      li {
        display: flex;
        align-items: center;
        gap: 15px;
        font-size: 18px;
        color: rgb(var(--text-light-color));

        .icon {
          font-size: 24px;
          line-height: 0;
          padding: 10px;
          background: rgb(var(--main-color));
          color: rgb(var(--title-light-color));
          border-radius: var(--normal-border-radius);
        }
      }
    }
  }
}

#projects {
  display: flex;
  flex-direction: column;
  gap: 60px;

  .group {
    display: flex;
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 30px;

    .section-head {
      flex: 1;

      h2 {
        max-width: 500px;
      }
    }

    .right {
      display: flex;
      flex-direction: column;
      gap: 20px;
      flex-wrap: wrap;

      p {
        font-size: 18px;
        color: rgb(var(--text-light-color));
        max-width: 600px;
      }

      .buttons {
        display: flex;
        gap: 20px;
        flex-wrap: wrap;
      }
    }
  }
}

@media (max-width: 1000px) {
  #about-us {
    flex-direction: column;
  }
}

@media (max-width: 400px) {
  #about-us .images {
    flex-direction: column;
  }
}

@media (max-width: 900px) {
  #hero {
    gap: 60px;

    .illustration {
      display: none;
    }

    .group {
      gap: 50px;

      .content {
        max-width: 100%;

        h1 {
          font-size: 36px;
        }
      }
    }
  }
}

@media (max-width: 1080px) {
  #why-us .content {
    .images {
      flex-direction: column;

      .img-box {
        aspect-ratio: 1;
        max-height: 300px;

        &:first-child {
          margin-bottom: 0;
        }

        &:last-child {
          margin-top: 0;
        }
      }
    }
  }
}

@media (max-width: 880px) {
  #why-us .content {
    flex-direction: column;
    align-items: center;

    .column {
      width: 100%;

      .images .img-box {
        max-width: 100%;
        height: 300px;
        aspect-ratio: 10;
      }
    }
  }
}
</style>